// React imports
import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';

// ** Custom Imports
import { accessibleOnClick } from '../../../utils';
import { PhoneNumberCrossIcon } from '../../../svg/phone-number-cross';
import { KEYPAD_KEYS, SANITIZE_IN_CALL_INPUT_REGEX } from '../utils/helper';

export interface DialerInCallKeypadProps {
  value: string;
  onChange: (value: string) => void;
  onSendDtmf: (digit: string) => void;
  /** When the parent sheet is open — focuses tone input without stealing focus while closed */
  isActive?: boolean;
}

/**
 * In-call DTMF input + keypad (digits, *, #). Value is controlled by the parent
 * so it persists when the panel is hidden.
 */
const DialerInCallKeypad: React.FC<DialerInCallKeypadProps> = ({
  isActive = false,
  value,
  onChange,
  onSendDtmf,
}) => {
  const phoneInputRef = useRef<HTMLInputElement>(null);

  /**
   * Keyboard digits use keydown preventDefault + controlled value; keypad uses the same path.
   * That bypasses native input scrolling, so scrollLeft stays 0 while the caret is at the end.
   * Sync horizontal scroll after the DOM reflects `value`.
   */
  useLayoutEffect(() => {
    const el = phoneInputRef.current;
    if (!el) return;
    el.scrollLeft = el.scrollWidth - el.clientWidth;
  }, [value]);

  useEffect(() => {
    if (!isActive) {
      return undefined;
    }
    const id = window.requestAnimationFrame(() => {
      phoneInputRef.current?.focus({ preventScroll: true });
    });
    return () => {
      window.cancelAnimationFrame(id);
    };
  }, [isActive]);

  const appendDtmfDigit = useCallback(
    (digit: string) => {
      if (!KEYPAD_KEYS?.includes(digit as typeof KEYPAD_KEYS[number])) return;
      onChange(value + digit);
      onSendDtmf(digit);
    },
    [onChange, onSendDtmf, value],
  );

  /** Paste / cut / delete only — typing uses keydown so repeat & stray keys are controlled */
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const sanitized =
        e?.target?.value?.replace(SANITIZE_IN_CALL_INPUT_REGEX, '') ?? '';
      const prev = value;

      if (sanitized?.length > prev?.length && sanitized?.startsWith(prev)) {
        const added = sanitized?.slice(prev?.length);
        added?.split('')?.forEach((ch) => {
          if (KEYPAD_KEYS?.includes(ch as typeof KEYPAD_KEYS[number])) {
            onSendDtmf(ch);
          }
        });
      }

      onChange(sanitized);
    },
    [onChange, onSendDtmf, value],
  );

  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const { key, repeat } = e;

      if (KEYPAD_KEYS?.includes(key as typeof KEYPAD_KEYS[number])) {
        if (repeat) {
          e.preventDefault();
          return;
        }
        e.preventDefault();
        appendDtmfDigit(key);
      }
    },
    [appendDtmfDigit],
  );

  const handleKeypadPointerDown = useCallback(
    (digit: string, e: React.PointerEvent<HTMLButtonElement>) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      e.preventDefault();
      appendDtmfDigit(digit);
    },
    [appendDtmfDigit],
  );

  const handleKeypadButtonKeyDown = useCallback(
    (digit: string, e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      if (e.repeat) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      appendDtmfDigit(digit);
    },
    [appendDtmfDigit],
  );

  const handleClearLast = useCallback(() => {
    const newValue = value?.slice(0, -1) ?? '';
    onChange(newValue);
    setTimeout(() => {
      phoneInputRef.current?.focus();
      const len = newValue.length;
      phoneInputRef.current?.setSelectionRange(len, len);
    }, 0);
  }, [onChange, value]);

  return (
    <div className="dialer-in-call-keypad">
      <div className="dialer-in-call-keypad-input-row">
        <input
          ref={phoneInputRef}
          type="tel"
          inputMode="tel"
          className="dialer-in-call-keypad-input"
          value={value}
          placeholder="Enter your response..."
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          aria-label="DTMF tones"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
        />
        <div
          className="dialer-in-call-keypad-input-clear"
          {...accessibleOnClick(handleClearLast)}
        >
          <PhoneNumberCrossIcon
            width={20}
            height={20}
            className="dialer-in-call-keypad-clear-icon"
          />
        </div>
      </div>
      <div className="dialer-in-call-keypad-grid dialer-keypad-grid">
        {KEYPAD_KEYS?.map((key) => (
          <button
            key={key}
            type="button"
            className="dialer-keypad-btn"
            onPointerDown={(e) => handleKeypadPointerDown(key, e)}
            onKeyDown={(e) => handleKeypadButtonKeyDown(key, e)}
          >
            <span
              className={`dialer-keypad-btn-number ${
                key === '*' ? 'dialer-keypad-btn-number-star' : ''
              }`}
            >
              {key}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default memo(DialerInCallKeypad);
